// Word and Outlook put a large amount of private markup on the clipboard: MSO
// conditional comments, `<o:p>` namespaced tags, `mso-*` style declarations and
// `Mso*` classes. Bulleted and numbered lists are the worst offender - Word does
// not emit <ul>/<ol> at all, it emits a run of paragraphs whose bullet glyph is
// baked into the text. Pasted straight into the editor that produces stray
// blank lines and bullets that are not really bullets.
//
// This module normalises a clipboard payload into the small subset of HTML the
// editor and the email wrapper both understand, before ProseMirror parses it.

const MSO_LIST_CLASS = /mso-?list/i;
const BULLET_MARKER = /^[\s ]*[·•▪◦o§*-][\s ]*/;
const ORDERED_MARKER = /^[\s ]*(\d+|[a-z]|[ivxlcdm]+)[.)][\s ]*/i;

const stripConditionalComments = (html = '') => {
  return (
    html
      // <!--[if gte mso 9]> ... <![endif]-->
      .replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '')
      // downlevel-revealed: <![if !supportLists]> ... <![endif]>
      .replace(/<!\[if[^\]]*\]>/gi, '')
      .replace(/<!\[endif\]>/gi, '')
      // Word's XML island
      .replace(/<xml[\s\S]*?<\/xml>/gi, '')
  );
};

const isWordListParagraph = (paragraph) => {
  const className = paragraph.getAttribute('class') || '';
  const style = paragraph.getAttribute('style') || '';

  return MSO_LIST_CLASS.test(className) || /mso-list\s*:/i.test(style);
};

// Word writes `mso-list: l0 level2 lfo1`. Fall back to the indent when it is
// missing, which is what Outlook produces for pasted mail.
const getListDepth = (paragraph) => {
  const style = paragraph.getAttribute('style') || '';
  const levelMatch = style.match(/mso-list\s*:[^;"']*level(\d+)/i);

  if (levelMatch) {
    return Math.max(0, Number(levelMatch[1]) - 1);
  }

  const indentMatch = style.match(/margin-left\s*:\s*([\d.]+)\s*(in|pt)/i);

  if (indentMatch) {
    const value = Number(indentMatch[1]);
    const inches = indentMatch[2].toLowerCase() === 'pt' ? value / 72 : value;
    return Math.max(0, Math.round(inches / 0.5) - 1);
  }

  return 0;
};

// The bullet glyph lives in its own span, usually with a Symbol or Wingdings
// font. Remove it so it does not end up as literal text in the list item.
const stripListMarker = (paragraph) => {
  const firstElement = paragraph.firstElementChild;

  if (firstElement) {
    const font = (firstElement.getAttribute('style') || '').toLowerCase();
    const isMarkerFont = /symbol|wingdings|courier new/.test(font);
    const text = (firstElement.textContent || '').trim();

    if (isMarkerFont || BULLET_MARKER.test(text) || ORDERED_MARKER.test(text)) {
      const remainder = text
        .replace(BULLET_MARKER, '')
        .replace(ORDERED_MARKER, '')
        .trim();

      if (remainder === '') {
        firstElement.remove();
        return;
      }
    }
  }

  // No dedicated marker element - trim the marker off the leading text node.
  const walker = paragraph.firstChild;

  if (walker && walker.nodeType === 3) {
    walker.nodeValue = walker.nodeValue.replace(BULLET_MARKER, '').replace(ORDERED_MARKER, '');
  }
};

const isOrderedListParagraph = (paragraph) => {
  const text = (paragraph.textContent || '').trim();

  if (BULLET_MARKER.test(text)) {
    return false;
  }

  return ORDERED_MARKER.test(text);
};

// Walk the document and fold consecutive Word list paragraphs into real
// <ul>/<ol> trees, honouring the level Word recorded.
const rebuildWordLists = (root) => {
  const paragraphs = Array.from(root.querySelectorAll('p'));
  let index = 0;

  while (index < paragraphs.length) {
    if (!isWordListParagraph(paragraphs[index])) {
      index += 1;
      continue;
    }

    const run = [];
    while (index < paragraphs.length && isWordListParagraph(paragraphs[index])) {
      run.push(paragraphs[index]);
      index += 1;
    }

    const ordered = isOrderedListParagraph(run[0]);
    const rootList = root.ownerDocument.createElement(ordered ? 'ol' : 'ul');
    run[0].parentNode.insertBefore(rootList, run[0]);

    // stack[n] is the list currently open at depth n.
    const stack = [rootList];

    run.forEach((paragraph) => {
      const depth = Math.min(getListDepth(paragraph), stack.length);

      while (stack.length > depth + 1) {
        stack.pop();
      }

      if (depth === stack.length) {
        const parentList = stack[stack.length - 1];
        const parentItem = parentList.lastElementChild;
        const nestedList = root.ownerDocument.createElement(
          isOrderedListParagraph(paragraph) ? 'ol' : 'ul',
        );

        if (parentItem) {
          parentItem.appendChild(nestedList);
        } else {
          parentList.appendChild(nestedList);
        }

        stack.push(nestedList);
      }

      stripListMarker(paragraph);

      const listItem = root.ownerDocument.createElement('li');
      const itemParagraph = root.ownerDocument.createElement('p');
      itemParagraph.innerHTML = paragraph.innerHTML;
      listItem.appendChild(itemParagraph);
      stack[stack.length - 1].appendChild(listItem);

      paragraph.remove();
    });
  }
};

const stripMsoStyles = (root) => {
  root.querySelectorAll('[style]').forEach((element) => {
    const cleaned = (element.getAttribute('style') || '')
      .split(';')
      .filter((declaration) => declaration.trim() && !/^\s*mso-/i.test(declaration))
      .join(';')
      .trim();

    if (cleaned) {
      element.setAttribute('style', cleaned);
    } else {
      element.removeAttribute('style');
    }
  });

  root.querySelectorAll('[class]').forEach((element) => {
    const kept = (element.getAttribute('class') || '')
      .split(/\s+/)
      .filter((name) => name && !/^Mso/i.test(name) && !/^Word/i.test(name));

    if (kept.length) {
      element.setAttribute('class', kept.join(' '));
    } else {
      element.removeAttribute('class');
    }
  });
};

const unwrap = (element) => {
  const parent = element.parentNode;

  if (!parent) {
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }

  parent.removeChild(element);
};

const stripUnusableNodes = (root) => {
  root.querySelectorAll('style, meta, link, title, script').forEach((node) => node.remove());

  // Namespaced Word/Excel tags (<o:p>, <w:sdt>, <m:oMath>) carry no content we
  // can use, but may wrap text we need to keep.
  Array.from(root.querySelectorAll('*')).forEach((element) => {
    if (element.tagName.includes(':')) {
      unwrap(element);
    }
  });

  // Comment nodes left behind by the conditional-comment strip.
  const iterator = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
  const comments = [];
  while (iterator.nextNode()) {
    comments.push(iterator.currentNode);
  }
  comments.forEach((comment) => comment.remove());
};

// A link that only resolves on the author's machine (`file:///...`) or relative
// to the Word document (`page.html`) is dead once the message is delivered.
// Keep the text, drop the destination, rather than inventing a hostname.
const dropUnresolvableLinks = (root) => {
  root.querySelectorAll('a[href]').forEach((link) => {
    const href = (link.getAttribute('href') || '').trim();

    if (!href) {
      link.removeAttribute('href');
      return;
    }

    if (/^(https?:|mailto:|tel:)/i.test(href) || href.startsWith('#') || href.startsWith('//')) {
      return;
    }

    link.removeAttribute('href');
  });
};

export const sanitizePastedHtml = (html = '') => {
  if (!html || typeof html !== 'string' || typeof document === 'undefined') {
    return html;
  }

  // Cheap exit for clipboard payloads that are not from Word/Outlook.
  const looksLikeOfficeMarkup = /mso-|Mso[A-Z]|<o:p|urn:schemas-microsoft-com|<!\[if/i.test(html);

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    looksLikeOfficeMarkup ? stripConditionalComments(html) : html,
    'text/html',
  );
  const root = doc.body;

  if (looksLikeOfficeMarkup) {
    stripUnusableNodes(root);
    rebuildWordLists(root);
    stripMsoStyles(root);
  }

  dropUnresolvableLinks(root);

  return root.innerHTML;
};

export default sanitizePastedHtml;
